import { useEffect, useMemo, useRef, useState } from "react";
import { Progress, SectionCard } from "@/features/power_projects/components/ui";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import type {
  AsyncTaskRecord,
  QuantitySummary,
  ZEParcelElementBreakdown,
  ZEProtectedAreaHit,
  ZETenderParcel,
  ZEWaterCrossingHit,
  ZETenderParcelDraft,
  ZETenderRoadNeighbor,
} from "../types";
import {
  fmtMeters,
  ownershipCodeColor,
  OWNERSHIP_OPTIONS,
  INTERNAL_ROAD_KIND,
  findOwnershipOption,
  protectedAreaColor,
  protectedAreaLabel,
} from "../utils";

/** Trailing segment of a TERYT id — the human-readable działka number (matches the
 *  backend's short_number derivation). */
function shortTeryt(teryt: string): string {
  return teryt.includes(".") ? teryt.split(".").pop()! : teryt;
}

function pluralDzialka(n: number): string {
  if (n === 1) return "działka";
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "działki";
  return "działek";
}

interface Props {
  parcels: ZETenderParcel[];
  drafts: ZETenderParcelDraft[];
  task: AsyncTaskRecord | undefined;
  isRecomputing: boolean;
  canRecompute: boolean;
  dirty: boolean;
  hasLines: boolean;
  /** Variant has at least one drawn point object — its parcel is discovered too. */
  hasObjects: boolean;
  onRecompute: () => void;
  /** Message to show when the recompute request itself failed (e.g. 500). */
  recomputeError?: string | null;
  /** MPZP coverage task — drives the "obliczanie…" spinner / error / cancel. */
  mpzpTask?: AsyncTaskRecord | undefined;
  onCancelMpzp?: () => void;
  isCancellingMpzp?: boolean;
  onFitToParcel: (teryt: string) => void;
  onParcelHighlight?: (teryt: string) => void; // scroll target from map "i" click
  highlightTeryt?: string | null;
  onSaveDraft: (
    teryt: string,
    data: { ownership_override?: string; wpw_count?: number | null },
  ) => void;
  onResetAllDrafts?: () => void;
  isResettingDrafts?: boolean;
  isFullscreen?: boolean;
  mpzpCoveragePct?: number | null;
  mpzpUnknownPct?: number | null;
  mpzpComputedAt?: string | null;
  showMpzpGaps?: boolean;
  onToggleMpzpGaps?: (next: boolean) => void;
  hasMpzpGap?: boolean;
  /** Whether there's anything (gap or "nie wiadomo") to draw on the map → show the toggle. */
  hasMpzpOverlay?: boolean;
  /** GDOŚ protected areas the route cuts — [] = analysed & clean, null = not analysed. */
  protectedAreas?: ZEProtectedAreaHit[] | null;
  protectedAreasCoveragePct?: number | null;
  protectedAreasComputedAt?: string | null;
  /** Protected-areas task — drives the "obliczanie…" spinner / error / cancel. */
  protectedAreasTask?: AsyncTaskRecord | undefined;
  onCancelProtectedAreas?: () => void;
  isCancellingProtectedAreas?: boolean;
  showProtectedAreas?: boolean;
  onToggleProtectedAreas?: (next: boolean) => void;
  hasProtectedAreasOverlay?: boolean;
  /** Przejścia trasy przez wody powierzchniowe (Hydroportal MPHP) — [] = policzone i
   *  czysto, null = nie liczono. Każde przejście to osobna pozycja cennika. */
  waterCrossings?: ZEWaterCrossingHit[] | null;
  waterCrossingsComputedAt?: string | null;
  waterCrossingsTask?: AsyncTaskRecord | undefined;
  onCancelWaterCrossings?: () => void;
  isCancellingWaterCrossings?: boolean;
  showWaterCrossings?: boolean;
  onToggleWaterCrossings?: (next: boolean) => void;
  hasWaterCrossingsOverlay?: boolean;
  /** Adjacent parcels of the marked internal roads (WPW Prywatny — droga wewn.). */
  roadNeighbors?: ZETenderRoadNeighbor[];
  /** Authoritative neighbour split from quantity-summary (ilosc_dz_prv_osc_dr_wewn*). */
  roadNeighborsInfo?: QuantitySummary["road_neighbors"];
  /** Road-neighbours task — drives the "obliczanie…" spinner / error. */
  roadNeighborsTask?: AsyncTaskRecord | undefined;
  onRecomputeRoadNeighbors?: () => void;
  isRecomputingRoadNeighbors?: boolean;
}

const breakdownValue = (b: ZEParcelElementBreakdown) =>
  b.length_m != null ? fmtMeters(b.length_m) : `${b.count} szt`;

const breakdownDot = (b: ZEParcelElementBreakdown) =>
  b.kind === "line" ? "#2563eb" : "#ea580c";

const LINE_CATEGORIES: {
  key: string;
  label: string;
  match: (code: string) => boolean;
}[] = [
  { key: "proj", label: "Projektowana", match: (c) => c.endsWith("_proj") },
  { key: "mod", label: "Modernizowana", match: (c) => c.endsWith("_mod") },
  { key: "likw", label: "Likwidowana", match: (c) => c.endsWith("_likw") },
];

function routeByCategory(parcels: ZETenderParcel[]) {
  const sums: Record<string, number> = {};
  for (const p of parcels) {
    for (const b of p.element_breakdown) {
      if (b.kind !== "line" || b.length_m == null) continue;
      const cat = LINE_CATEGORIES.find((c) => c.match(b.element_type));
      if (!cat) continue;
      sums[cat.key] = (sums[cat.key] ?? 0) + b.length_m;
    }
  }
  return LINE_CATEGORIES.map((c) => ({
    key: c.key,
    label: c.label,
    length_m: Math.round((sums[c.key] ?? 0) * 100) / 100,
  })).filter((c) => c.length_m > 0);
}

function parseProgress(log: string | undefined) {
  if (!log) return null;
  const matches = [...log.matchAll(/\[(\d+)\/(\d+)\]/g)];
  if (!matches.length) return null;
  const last = matches[matches.length - 1];
  const current = Number(last[1]);
  const total = Number(last[2]);
  if (!total) return null;
  return { current, total, pct: Math.min(100, (current / total) * 100) };
}

function StatChip({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="bg-[var(--color-bg-surface)] px-2.5 py-1.5">
      <p className="section-label leading-none">{label}</p>
      <p className="font-mono text-sm font-bold text-[var(--color-text-primary)] leading-none mt-1.5">
        {value}
      </p>
    </div>
  );
}

// ── Office summary ────────────────────────────────────────────────────────────

interface OfficeSummaryRow {
  label: string;
  parcels: number;
  offices: number | null; // null = nie liczymy urzędów (WPW, parcel)
  wpwCount: number | null; // suma szacowanych zgód WPW
}

/** Obręb TERYT has exactly 1 dot ("220408_2.0015"); działka has 2+ ("220401_1.0008.2/19"). */
const isObrebTeryt = (teryt: string) => (teryt.match(/\./g) ?? []).length === 1;

/** Extract 6-digit commune code from TERYT (e.g. "220401_1.0009.1/44" → "220401"). */
const terytGmina = (teryt: string) => teryt.split("_")[0] ?? teryt;
/** Extract 4-digit powiat code. */
const terytPowiat = (teryt: string) => terytGmina(teryt).slice(0, 4);
/** Extract 2-digit voivodeship code. */
const terytWoj = (teryt: string) => terytGmina(teryt).slice(0, 2);

/** True if a parcel has at least one non-liquidation drawn element (line or object).
 *  Parcels with empty breakdown are included (route crosses them, type unknown). */
function hasNonLikwElements(p: ZETenderParcel): boolean {
  if (!p.element_breakdown.length) return true;
  return p.element_breakdown.some((b) => !b.element_type.endsWith("_likw"));
}

function buildOfficesSummary(
  parcels: ZETenderParcel[],
  drafts: ZETenderParcelDraft[],
): OfficeSummaryRow[] {
  const draftByTeryt = new Map(drafts.map((d) => [d.teryt, d]));

  // Per-ownership accumulator: unique office keys + WPW sum + parcel count
  const acc = new Map<
    string,
    {
      label: string;
      uniqueKeys: Set<string>;
      wpwSum: number;
      parcelCount: number;
      isWpw: boolean;
    }
  >();

  const ensure = (optValue: string, label: string, isWpw: boolean) => {
    if (!acc.has(optValue))
      acc.set(optValue, {
        label,
        uniqueKeys: new Set(),
        wpwSum: 0,
        parcelCount: 0,
        isWpw,
      });
    return acc.get(optValue)!;
  };

  for (const p of parcels.filter(hasNonLikwElements)) {
    const draft = draftByTeryt.get(p.teryt);
    const effectiveValue = draft?.ownership_override || p.ownership_code;
    if (!effectiveValue) continue;

    const opt = findOwnershipOption(effectiveValue);
    const label = opt?.label ?? effectiveValue;
    const isWpw = opt?.isWpw ?? false;
    const level = opt?.uniqueLevel ?? "parcel";
    const row = ensure(effectiveValue, label, isWpw);
    row.parcelCount += 1;

    // Unique office key
    let key: string;
    if (level === "gmina") key = terytGmina(p.teryt);
    else if (level === "powiat") key = terytPowiat(p.teryt);
    else if (level === "woj") key = terytWoj(p.teryt);
    else key = p.teryt; // parcel or wpw → each parcel is its own entry
    row.uniqueKeys.add(key);

    // WPW count
    if (isWpw) {
      const count = draft?.wpw_count ?? opt?.wpwDefault ?? 1;
      row.wpwSum += count;
    }
  }

  const rows: OfficeSummaryRow[] = [];
  for (const [, v] of acc) {
    const opt = findOwnershipOption(
      [...acc.entries()].find(([, r]) => r === v)?.[0] ?? "",
    );
    const level = opt?.uniqueLevel ?? "parcel";
    rows.push({
      label: v.label,
      parcels: v.parcelCount,
      offices: level === "wpw" || level === "parcel" ? null : v.uniqueKeys.size,
      wpwCount: v.isWpw ? Math.round(v.wpwSum * 10) / 10 : null,
    });
  }
  // Sort: offices first (by parcel count desc), then wpw, then other
  return rows.sort((a, b) => {
    if ((a.offices !== null) !== (b.offices !== null))
      return a.offices !== null ? -1 : 1;
    return b.parcels - a.parcels;
  });
}

// ─────────────────────────────────────────────────────────────────────────────

export function ZEParcelsPanel({
  parcels,
  drafts = [],
  task,
  isRecomputing,
  canRecompute,
  dirty,
  hasLines,
  hasObjects,
  onRecompute,
  recomputeError,
  mpzpTask,
  onCancelMpzp,
  isCancellingMpzp = false,
  onFitToParcel,
  highlightTeryt,
  onSaveDraft,
  onResetAllDrafts,
  isResettingDrafts = false,
  isFullscreen = false,
  mpzpCoveragePct,
  mpzpUnknownPct,
  mpzpComputedAt,
  showMpzpGaps = true,
  onToggleMpzpGaps,
  hasMpzpGap = false,
  hasMpzpOverlay = false,
  protectedAreas,
  protectedAreasCoveragePct,
  protectedAreasComputedAt,
  protectedAreasTask,
  onCancelProtectedAreas,
  isCancellingProtectedAreas = false,
  showProtectedAreas = true,
  onToggleProtectedAreas,
  hasProtectedAreasOverlay = false,
  waterCrossings,
  waterCrossingsComputedAt,
  waterCrossingsTask,
  onCancelWaterCrossings,
  isCancellingWaterCrossings = false,
  showWaterCrossings = true,
  onToggleWaterCrossings,
  hasWaterCrossingsOverlay = false,
  roadNeighbors = [],
  roadNeighborsInfo,
  roadNeighborsTask,
  onRecomputeRoadNeighbors,
  isRecomputingRoadNeighbors = false,
}: Props) {
  const running =
    isRecomputing || (task && ["pending", "running"].includes(task.status));
  const progress = running ? parseProgress(task?.log) : null;

  const logRef = useRef<HTMLPreElement>(null);
  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [task?.log]);

  // Auto-scroll to highlighted parcel row (triggered by map "i" click).
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
  useEffect(() => {
    if (!highlightTeryt) return;
    const el = rowRefs.current.get(highlightTeryt);
    el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [highlightTeryt]);

  // Działki z 2+ kropkami w TERYT to prawdziwe numery ewidencyjne.
  // Obręby (1 kropka) są wskazówkami AI — chowamy je gdy mamy prawdziwe działki.
  const realParcels = parcels.filter((p) => !isObrebTeryt(p.teryt));
  const obrebParcels = parcels.filter((p) => isObrebTeryt(p.teryt));
  const hasRealParcels = realParcels.length > 0;
  // When real parcels exist, obrebs are superseded; otherwise show them as hints.
  const displayedParcels = hasRealParcels ? realParcels : parcels;

  const totalLen = displayedParcels.reduce(
    (acc, p) => acc + (p.intersection_length_m ?? 0),
    0,
  );
  const maxLen = Math.max(
    1,
    ...displayedParcels.map((p) => p.intersection_length_m ?? 0),
  );
  const routeCats = routeByCategory(displayedParcels);
  const officesSummary = buildOfficesSummary(displayedParcels, drafts);

  // Liczby przejść przez wody per pozycja cennika — dokładnie to, co backend wystawia
  // jako klucze presetowe przejscie_wp_ziemne / przejscie_wp_napowietrzne.
  const waterCounts = useMemo(() => {
    const hits = waterCrossings ?? [];
    return {
      ziemne: hits.filter((h) => h.key === "przejscie_wp_ziemne").length,
      napowietrzne: hits.filter((h) => h.key === "przejscie_wp_napowietrzne").length,
    };
  }, [waterCrossings]);

  const draftByTeryt = new Map(drafts.map((d) => [d.teryt, d]));

  // Internal roads (WPW Prywatny — droga wewn.) — the neighbour counts feed the
  // ilosc_dz_prv_osc_dr_wewn* pricing keys. A road marked AFTER the last recompute has
  // no neighbour rows yet → prompt the "Przelicz sąsiadów dróg" button.
  const parcelTeryts = useMemo(() => new Set(parcels.map((p) => p.teryt)), [parcels]);
  const markedRoadTeryts = drafts
    .filter((d) => d.ownership_override === INTERNAL_ROAD_KIND && parcelTeryts.has(d.teryt))
    .map((d) => d.teryt);
  const roadsWithRows = new Set(roadNeighbors.map((n) => n.road_teryt));
  const roadsMissingNeighbors = markedRoadTeryts.filter((t) => !roadsWithRows.has(t));
  // Neighbour rows hold EVERY parcel touching the road (public ones included) — but the
  // ilosc_dz_prv_osc_dr_wewn* keys only count private (7/15, incl. wpw_dzialka_koncowa)
  // or unknown-ownership neighbours that are not themselves marked roads. Mirror the
  // backend rule so the per-road line shows "counted X of N" instead of a raw row count
  // that visibly disagrees with the split above.
  const allMarkedRoadTeryts = new Set(
    drafts.filter((d) => d.ownership_override === INTERNAL_ROAD_KIND).map((d) => d.teryt),
  );
  const isCountedNeighbor = (teryt: string, code: string): boolean => {
    if (allMarkedRoadTeryts.has(teryt)) return false;
    const effective = (draftByTeryt.get(teryt)?.ownership_override || code || "").trim();
    return (
      !effective || effective === "7" || effective === "15" ||
      effective === "wpw_dzialka_koncowa"
    );
  };
  const roadNeighborsRunning =
    isRecomputingRoadNeighbors ||
    (roadNeighborsTask && ["pending", "running"].includes(roadNeighborsTask.status));

  type SortCol = "teryt" | "owner" | "trasa";
  type SortDir = "asc" | "desc";
  const [sort, setSort] = useState<{ col: SortCol; dir: SortDir } | null>(null);

  const toggleSort = (col: SortCol) =>
    setSort((s) => s?.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: "asc" });

  const sortedParcels = useMemo(() => {
    if (!sort) return displayedParcels;
    return [...displayedParcels].sort((a, b) => {
      let cmp = 0;
      if (sort.col === "teryt") {
        cmp = a.teryt.localeCompare(b.teryt, "pl");
      } else if (sort.col === "trasa") {
        cmp = (a.intersection_length_m ?? 0) - (b.intersection_length_m ?? 0);
      } else {
        const ea = draftByTeryt.get(a.teryt)?.ownership_override || a.ownership_code;
        const eb = draftByTeryt.get(b.teryt)?.ownership_override || b.ownership_code;
        cmp = ea.localeCompare(eb, "pl");
      }
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [displayedParcels, sort, draftByTeryt]);

  // Hide internal summary lines (the "Zakończono: …" rollup with KIEG counts) from the UI log.
  const cleanLog = task?.log
    ?.split("\n")
    .filter((l) => !l.startsWith("Zakończono:"))
    .join("\n");
  const logPre = cleanLog ? (
    <pre
      ref={logRef}
      className="whitespace-pre-wrap break-words max-h-40 overflow-y-auto p-2.5 text-[10.5px] leading-relaxed font-mono text-[var(--color-text-secondary)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-muted)]"
    >
      {cleanLog}
    </pre>
  ) : null;

  return (
    <SectionCard title="Działki pod trasą">
      <Button
        className="w-full"
        disabled={!canRecompute || !!running}
        loading={!!running}
        onClick={onRecompute}
      >
        {!running && (
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            aria-hidden
          >
            <path
              d="M2 4.5l4.5-2 4 2 3.5-1.5v9l-3.5 1.5-4-2-4.5 2v-9z"
              strokeLinejoin="round"
            />
            <path d="M6.5 2.5v11M10.5 4.5v9" />
          </svg>
        )}
        Przelicz działki (ULDK)
      </Button>

      {!hasLines && !hasObjects && (
        <p className="text-[11px] font-mono text-[var(--color-text-muted)] mt-2">
          Narysuj trasę (linię) lub postaw obiekt, aby przeliczyć działki.
        </p>
      )}
      {(hasLines || hasObjects) && dirty && (
        <p className="text-[11px] font-mono text-amber-600 mt-2 flex items-center gap-1.5">
          <span className="inline-block w-1.5 h-1.5 bg-amber-600 shrink-0" />
          Zapisz rysunek przed przeliczeniem.
        </p>
      )}

      {recomputeError && (
        <div className="mt-2 flex items-start gap-2 px-2.5 py-2 border-l-2 border-red-600 bg-red-600/10">
          <span className="text-red-600 font-bold leading-none mt-0.5">⚠</span>
          <span className="text-[11px] font-mono text-red-600">
            Nie udało się uruchomić przeliczania: {recomputeError}
          </span>
        </div>
      )}

      {task && (
        <div className="mt-3">
          {task.status === "success" ? (
            <div className="flex items-center gap-2 px-2.5 py-1.5 border-l-2 border-emerald-600 bg-[var(--color-bg-elevated)]">
              <span className="text-emerald-600 font-bold">✓</span>
              <span className="text-xs font-mono text-[var(--color-text-secondary)]">
                Gotowe —{" "}
                <b className="text-[var(--color-text-primary)]">
                  {realParcels.length || parcels.length}
                </b>{" "}
                {pluralDzialka(realParcels.length || parcels.length)} na mapie{hasRealParcels && obrebParcels.length > 0 ? ` (pominięto ${obrebParcels.length} obr.)` : ""}.
              </span>
            </div>
          ) : task.status === "failure" ? (
            <div className="flex items-center gap-2 px-2.5 py-1.5 border-l-2 border-[var(--color-primary)] bg-[var(--color-primary-dim)]">
              <span className="text-[var(--color-primary)] font-bold">!</span>
              <span className="text-xs font-mono text-[var(--color-primary)]">
                Błąd przeliczania — sprawdź log.
              </span>
            </div>
          ) : (
            <div className="px-2.5 py-2 border-l-2 border-[var(--color-primary)] bg-[var(--color-bg-elevated)]">
              <div className="flex items-center gap-2">
                <Spinner size="sm" />
                <span className="text-xs font-mono text-[var(--color-text-secondary)]">
                  Liczę działki z ULDK…
                </span>
                {progress && (
                  <span className="ml-auto font-mono text-xs font-bold text-[var(--color-text-primary)]">
                    {progress.current}
                    <span className="text-[var(--color-text-muted)] font-normal">
                      /{progress.total}
                    </span>
                  </span>
                )}
              </div>
              {progress ? (
                <Progress pct={progress.pct} className="mt-2" />
              ) : (
                <p className="text-[10px] font-mono text-[var(--color-text-muted)] mt-1.5">
                  próbkowanie korytarza trasy…
                </p>
              )}
            </div>
          )}

          {logPre && (running || task.status === "failure") && (
            <div className="mt-2 space-y-1">
              <span className="section-label">Log ULDK</span>
              {logPre}
            </div>
          )}
          {logPre && task.status === "success" && (
            <details className="mt-2 group">
              <summary className="section-label cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden flex items-center gap-1 hover:text-[var(--color-primary)]">
                <span className="transition-transform group-open:rotate-90">
                  ▸
                </span>{" "}
                Log ULDK
              </summary>
              <div className="mt-1.5">{logPre}</div>
            </details>
          )}
        </div>
      )}

      {parcels.length > 0 && (
        <>
          {/* Info when obrebs are superseded by real parcels */}
          {hasRealParcels && obrebParcels.length > 0 && (
            <p className="mt-2 text-[10px] font-mono text-[var(--color-text-muted)]">
              Ukryto {obrebParcels.length} wpis{obrebParcels.length === 1 ? "" : "y"} na poziomie obrębu — zastąpione przez działki ewidencyjne.
            </p>
          )}

          {/* ── summary strip ── */}
          <div className="grid grid-cols-2 gap-px bg-[var(--color-border)] border border-[var(--color-border)] mt-3">
            <StatChip label="Działki" value={displayedParcels.length} />
            <StatChip label="Trasa łącznie" value={fmtMeters(totalLen)} />
          </div>

          {/* ── MPZP coverage ── */}
          {mpzpComputedAt != null && mpzpCoveragePct != null ? (
            <div className="mt-2">
              <div className="flex items-center justify-between font-mono text-[11px] mb-1">
                <span className="section-label">Pokrycie MPZP</span>
                <span
                  className={`font-bold ${
                    mpzpCoveragePct >= 80
                      ? "text-emerald-600"
                      : mpzpCoveragePct >= 40
                        ? "text-amber-600"
                        : "text-[var(--color-primary)]"
                  }`}
                >
                  {mpzpCoveragePct.toFixed(1)}%
                </span>
              </div>
              <div className="h-1.5 bg-[var(--color-border)] w-full">
                <div
                  className={`h-full transition-all ${
                    mpzpCoveragePct >= 80
                      ? "bg-emerald-600"
                      : mpzpCoveragePct >= 40
                        ? "bg-amber-500"
                        : "bg-[var(--color-primary)]"
                  }`}
                  style={{ width: `${mpzpCoveragePct}%` }}
                />
              </div>
              {/* Red warning only for CONFIRMED gaps (data exists, no MPZP). Areas with no
                  registry data are "nie wiadomo", not a gap — they get the neutral note below. */}
              {hasMpzpGap && (
                <div className="mt-1.5 flex items-center gap-1.5 px-2.5 py-2 bg-red-600 text-white font-mono font-bold text-[11px] tracking-wide">
                  <span className="text-base leading-none">⚠</span>
                  BRAKI W MPZP — KONIECZNE ULICP
                </div>
              )}
              {(mpzpUnknownPct ?? 0) > 0 && (
                <div className="mt-1.5 flex items-center gap-1.5 px-2.5 py-2 bg-gray-500 text-white font-mono font-bold text-[11px] tracking-wide">
                  <span className="text-base leading-none">?</span>
                  BRAK DANYCH MPZP — NIE MOŻNA USTALIĆ ({mpzpUnknownPct!.toFixed(1)}%)
                </div>
              )}
              {hasMpzpOverlay && onToggleMpzpGaps && (
                <label className="mt-1.5 flex items-center gap-1.5 cursor-pointer select-none font-mono text-[11px] text-[var(--color-text-secondary)]">
                  <input
                    type="checkbox"
                    className="accent-red-600"
                    checked={showMpzpGaps}
                    onChange={(e) => onToggleMpzpGaps(e.target.checked)}
                  />
                  Pokaż braki / „nie wiadomo" MPZP na mapie
                </label>
              )}
            </div>
          ) : mpzpTask?.status === "failure" ? (
            <p className="mt-2 text-[11px] font-mono text-red-600 flex items-start gap-1.5">
              <span className="font-bold leading-none mt-0.5">⚠</span>
              <span>
                MPZP: obliczenie nie powiodło się
                {mpzpTask.error ? ` — ${mpzpTask.error}` : "."} Przelicz działki,
                aby spróbować ponownie.
              </span>
            </p>
          ) : mpzpTask?.status === "cancelled" ? (
            <p className="mt-2 text-[11px] font-mono text-[var(--color-text-muted)] flex items-center gap-1.5">
              MPZP: obliczanie przerwane.
            </p>
          ) : isRecomputing ||
            mpzpTask?.status === "pending" ||
            mpzpTask?.status === "running" ? (
            <div className="mt-2 flex items-center gap-2.5">
              <p className="text-[11px] font-mono text-[var(--color-text-muted)] flex items-center gap-1.5">
                <Spinner size="sm" className="w-3 h-3" />
                MPZP: obliczanie…
              </p>
              {onCancelMpzp &&
                (mpzpTask?.status === "pending" ||
                  mpzpTask?.status === "running") && (
                  <button
                    type="button"
                    onClick={onCancelMpzp}
                    disabled={isCancellingMpzp}
                    className="text-[11px] font-mono text-red-600 hover:underline disabled:opacity-50 disabled:no-underline"
                  >
                    {isCancellingMpzp ? "Przerywanie…" : "Przerwij"}
                  </button>
                )}
            </div>
          ) : mpzpComputedAt == null && parcels.length > 0 ? (
            <p className="mt-2 text-[11px] font-mono text-[var(--color-text-muted)] flex items-center gap-1.5">
              MPZP: nie obliczono — użyj „Przelicz działki".
            </p>
          ) : null}

          {/* ── Obszary chronione (GDOŚ) — INVERTED vs MPZP: any hit is a problem ── */}
          {protectedAreasComputedAt != null && protectedAreas != null ? (
            <div className="mt-2">
              {protectedAreas.length > 0 ? (
                <>
                  <div className="flex items-center justify-between font-mono text-[11px] mb-1">
                    <span className="section-label">Obszary chronione (GDOŚ)</span>
                    {protectedAreasCoveragePct != null && (
                      <span className="font-bold text-red-600">
                        {protectedAreasCoveragePct.toFixed(1)}% trasy
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 px-2.5 py-2 bg-red-600 text-white font-mono font-bold text-[11px] tracking-wide">
                    <span className="text-base leading-none">⚠</span>
                    OBSZARY CHRONIONE NA TRASIE
                  </div>
                  {/* One row per (form type, area) — overlapping forms are NEVER collapsed:
                      a Rezerwat inside a Park Krajobrazowy shows as two rows, each with its
                      full own metres, cross-referenced via "pokrywa się z". */}
                  <ul className="border border-t-0 border-[var(--color-border)] divide-y divide-[var(--color-border)]">
                    {protectedAreas.map((hit) => (
                      <li
                        key={`${hit.layer_key}-${hit.gid ?? hit.nazwa}`}
                        className="px-2.5 py-1.5 font-mono text-[11px] flex items-start gap-1.5"
                      >
                        <span
                          className="mt-0.5 inline-block w-2.5 h-2.5 shrink-0 border border-black/30"
                          style={{ background: protectedAreaColor(hit.layer_key) }}
                        />
                        <span className="text-[var(--color-text-primary)]">
                          <span className="font-bold">{hit.type_label}</span>
                          {hit.nazwa ? <> «{hit.nazwa}»</> : null}{" "}
                          <span className="font-bold">({fmtMeters(hit.route_length_m)})</span>
                          {(hit.overlaps_with?.length ?? 0) > 0 && (
                            <span className="text-[var(--color-text-muted)]">
                              {" "}· pokrywa się z:{" "}
                              {hit.overlaps_with.map(protectedAreaLabel).join(", ")}
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="text-[11px] font-mono text-emerald-700 flex items-center gap-1.5">
                  <span className="font-bold">✓</span>
                  Trasa poza obszarami chronionymi (GDOŚ).
                </p>
              )}
              {hasProtectedAreasOverlay && onToggleProtectedAreas && (
                <label className="mt-1.5 flex items-center gap-1.5 cursor-pointer select-none font-mono text-[11px] text-[var(--color-text-secondary)]">
                  <input
                    type="checkbox"
                    className="accent-green-700"
                    checked={showProtectedAreas}
                    onChange={(e) => onToggleProtectedAreas(e.target.checked)}
                  />
                  Pokaż obszary chronione na mapie
                </label>
              )}
            </div>
          ) : protectedAreasTask?.status === "failure" ? (
            <p className="mt-2 text-[11px] font-mono text-red-600 flex items-start gap-1.5">
              <span className="font-bold leading-none mt-0.5">⚠</span>
              <span>
                Obszary chronione: obliczenie nie powiodło się
                {protectedAreasTask.error ? ` — ${protectedAreasTask.error}` : "."} Przelicz
                działki, aby spróbować ponownie.
              </span>
            </p>
          ) : protectedAreasTask?.status === "cancelled" ? (
            <p className="mt-2 text-[11px] font-mono text-[var(--color-text-muted)] flex items-center gap-1.5">
              Obszary chronione: obliczanie przerwane.
            </p>
          ) : isRecomputing ||
            protectedAreasTask?.status === "pending" ||
            protectedAreasTask?.status === "running" ? (
            <div className="mt-2 flex items-center gap-2.5">
              <p className="text-[11px] font-mono text-[var(--color-text-muted)] flex items-center gap-1.5">
                <Spinner size="sm" className="w-3 h-3" />
                Obszary chronione: obliczanie…
              </p>
              {onCancelProtectedAreas &&
                (protectedAreasTask?.status === "pending" ||
                  protectedAreasTask?.status === "running") && (
                  <button
                    type="button"
                    onClick={onCancelProtectedAreas}
                    disabled={isCancellingProtectedAreas}
                    className="text-[11px] font-mono text-red-600 hover:underline disabled:opacity-50 disabled:no-underline"
                  >
                    {isCancellingProtectedAreas ? "Przerywanie…" : "Przerwij"}
                  </button>
                )}
            </div>
          ) : protectedAreasComputedAt == null && parcels.length > 0 ? (
            <p className="mt-2 text-[11px] font-mono text-[var(--color-text-muted)] flex items-center gap-1.5">
              Obszary chronione: nie obliczono — użyj „Przelicz działki".
            </p>
          ) : null}

          {/* ── Przejścia przez wody (Hydroportal) — SZTUKI, nie procenty: każde
                 przejście to osobna pozycja cennika („Przejście WP" doziemne 4500 zł /
                 napowietrzne 3000 zł), więc panel pokazuje liczby, nie pokrycie trasy. ── */}
          {waterCrossingsComputedAt != null && waterCrossings != null ? (
            <div className="mt-2">
              {waterCrossings.length > 0 ? (
                <>
                  <div className="flex items-center justify-between font-mono text-[11px] mb-1">
                    <span className="section-label">Przejścia przez wody (Wody Polskie)</span>
                    <span className="font-bold text-blue-700">
                      {waterCounts.ziemne} doziem. · {waterCounts.napowietrzne} napow.
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 px-2.5 py-2 bg-blue-700 text-white font-mono font-bold text-[11px] tracking-wide">
                    <span className="text-base leading-none">≈</span>
                    PRZEJŚCIA PRZEZ WODY — ZGODA/POZWOLENIE WODNOPRAWNE
                  </div>
                  {/* Jeden wiersz = jedna pozycja cennika. Ciek przecięty i kablem, i linią
                      napowietrzną daje DWA wiersze — to dwie różne stawki. */}
                  <ul className="border border-t-0 border-[var(--color-border)] divide-y divide-[var(--color-border)]">
                    {waterCrossings.map((hit, i) => (
                      <li
                        key={`${hit.feature_ref}-${hit.key}-${i}`}
                        className="px-2.5 py-1.5 font-mono text-[11px] flex items-start gap-1.5"
                      >
                        <span
                          className="mt-0.5 inline-block w-2.5 h-2.5 shrink-0 border border-black/30"
                          style={{
                            background:
                              hit.key === "przejscie_wp_napowietrzne" ? "#0ea5e9" : "#1d4ed8",
                          }}
                        />
                        <span className="text-[var(--color-text-primary)]">
                          <span className="font-bold">{hit.kind_label}</span>
                          {hit.feature_name ? <> «{hit.feature_name}»</> : null}
                          {hit.element_label ? (
                            <span className="text-[var(--color-text-muted)]">
                              {" "}· {hit.element_label}
                            </span>
                          ) : null}
                          {hit.needs_review && (
                            <span className="ml-1 font-bold text-amber-600">
                              — SPRAWDŹ RODZAJ
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="text-[11px] font-mono text-emerald-700 flex items-center gap-1.5">
                  <span className="font-bold">✓</span>
                  Trasa nie przekracza wód powierzchniowych (Hydroportal).
                </p>
              )}
              {hasWaterCrossingsOverlay && onToggleWaterCrossings && (
                <label className="mt-1.5 flex items-center gap-1.5 cursor-pointer select-none font-mono text-[11px] text-[var(--color-text-secondary)]">
                  <input
                    type="checkbox"
                    className="accent-blue-700"
                    checked={showWaterCrossings}
                    onChange={(e) => onToggleWaterCrossings(e.target.checked)}
                  />
                  Pokaż przejścia przez wody na mapie
                </label>
              )}
            </div>
          ) : waterCrossingsTask?.status === "failure" ? (
            <p className="mt-2 text-[11px] font-mono text-red-600 flex items-start gap-1.5">
              <span className="font-bold leading-none mt-0.5">⚠</span>
              <span>
                Przejścia przez wody: obliczenie nie powiodło się
                {waterCrossingsTask.error ? ` — ${waterCrossingsTask.error}` : "."} Przelicz
                działki, aby spróbować ponownie (pusty wynik NIE oznacza czystej trasy).
              </span>
            </p>
          ) : waterCrossingsTask?.status === "cancelled" ? (
            <p className="mt-2 text-[11px] font-mono text-[var(--color-text-muted)] flex items-center gap-1.5">
              Przejścia przez wody: obliczanie przerwane.
            </p>
          ) : isRecomputing ||
            waterCrossingsTask?.status === "pending" ||
            waterCrossingsTask?.status === "running" ? (
            <div className="mt-2 flex items-center gap-2.5">
              <p className="text-[11px] font-mono text-[var(--color-text-muted)] flex items-center gap-1.5">
                <Spinner size="sm" className="w-3 h-3" />
                Przejścia przez wody: obliczanie…
              </p>
              {onCancelWaterCrossings &&
                (waterCrossingsTask?.status === "pending" ||
                  waterCrossingsTask?.status === "running") && (
                  <button
                    type="button"
                    onClick={onCancelWaterCrossings}
                    disabled={isCancellingWaterCrossings}
                    className="text-[11px] font-mono text-red-600 hover:underline disabled:opacity-50 disabled:no-underline"
                  >
                    {isCancellingWaterCrossings ? "Przerywanie…" : "Przerwij"}
                  </button>
                )}
            </div>
          ) : waterCrossingsComputedAt == null && parcels.length > 0 ? (
            <p className="mt-2 text-[11px] font-mono text-[var(--color-text-muted)] flex items-center gap-1.5">
              Przejścia przez wody: nie obliczono — użyj „Przelicz działki".
            </p>
          ) : null}

          {routeCats.length > 0 && (
            <div className="mt-2">
              <p className="section-label mb-1">Trasa wg rodzaju</p>
              <ul className="space-y-0.5 text-[11px] font-mono">
                {routeCats.map((c) => (
                  <li
                    key={c.key}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="text-[var(--color-text-secondary)]">
                      {c.label}
                    </span>
                    <span className="font-bold text-[var(--color-text-primary)]">
                      {fmtMeters(c.length_m)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* ── global reset ── */}
          {onResetAllDrafts && drafts.length > 0 && (
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={onResetAllDrafts}
                disabled={isResettingDrafts}
                className="inline-flex items-center gap-1.5 text-[10px] font-mono text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors underline decoration-dotted disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResettingDrafts ? <Spinner size="sm" /> : <span>↺</span>}
                resetuj nadpisania wszystkich działek do domyślnych ULDK
              </button>
            </div>
          )}

          {/* ── parcels table ── */}
          <div className={`mt-3 border border-[var(--color-border)] overflow-auto${isFullscreen ? "" : " max-h-[520px]"}`}>
            <table className="w-full text-[11px] font-mono border-collapse">
              <thead>
                <tr>
                  {(
                    [
                      { k: "teryt" as SortCol, label: "TERYT", cls: "" },
                      { k: "owner" as SortCol, label: "Własność", cls: "" },
                      { k: "trasa" as SortCol, label: "Trasa", cls: "text-right" },
                    ] as { k: SortCol; label: string; cls: string }[]
                  ).map((c) => {
                    const active = sort?.col === c.k;
                    return (
                      <th
                        key={c.k}
                        onClick={() => toggleSort(c.k)}
                        className={`sticky top-0 z-10 bg-[var(--color-bg-elevated)] px-2 py-1.5 text-left font-bold uppercase tracking-widest text-[0.6rem] shadow-[inset_0_-1px_0_var(--color-border)] cursor-pointer select-none transition-colors hover:text-[var(--color-primary)] ${c.cls} ${active ? "text-[var(--color-primary)]" : "text-[var(--color-text-muted)]"}`}
                      >
                        <span className="inline-flex items-center gap-1">
                          {c.label}
                          <span className={`text-[9px] leading-none ${active ? "opacity-100" : "opacity-20"}`}>
                            {active && sort?.dir === "desc" ? "▼" : "▲"}
                          </span>
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedParcels.map((p) => {
                  const draft = draftByTeryt.get(p.teryt);
                  const overrideValue = draft?.ownership_override ?? "";
                  const overrideOpt = overrideValue
                    ? findOwnershipOption(overrideValue)
                    : undefined;
                  const effectiveOpt =
                    overrideOpt ?? findOwnershipOption(p.ownership_code);
                  const isWpw = effectiveOpt?.isWpw ?? false;
                  const isObreb = isObrebTeryt(p.teryt);
                  const wpwDefault = effectiveOpt?.wpwDefault ?? 1.5;
                  const isHighlighted = p.teryt === highlightTeryt;

                  return (
                    <tr
                      key={p.id}
                      ref={(el) => {
                        if (el) rowRefs.current.set(p.teryt, el);
                        else rowRefs.current.delete(p.teryt);
                      }}
                      className={`align-top transition-colors ${
                        isHighlighted
                          ? "bg-[var(--color-primary-dim)] ring-1 ring-inset ring-[var(--color-primary)]"
                          : "even:bg-[var(--color-bg-base)] hover:bg-[var(--color-primary-dim)]"
                      }`}
                    >
                      {/* TERYT + Przybliż */}
                      <td className={`px-2 ${isHighlighted ? "py-3" : "py-1.5"} align-top min-w-[140px]`}>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[var(--color-text-primary)] font-medium whitespace-nowrap">
                            {p.teryt}
                          </span>
                          {isObreb && (
                            <span className="inline-block px-1 py-px text-[9px] font-mono uppercase tracking-wider border border-[var(--color-border)] text-[var(--color-text-muted)] leading-none">
                              obręb
                            </span>
                          )}
                          {p.mpzp_gap === true && (
                            <span title="Długość trasy w tej działce bez pokrycia MPZP" className="text-red-600 font-bold leading-none text-[11px]">
                              ✕MPZP{p.mpzp_gap_length_m != null ? ` ${fmtMeters(p.mpzp_gap_length_m)}` : ""}
                            </span>
                          )}
                          {p.mpzp_unknown === true && (
                            <span title="Długość trasy w tej działce bez danych MPZP (nie można ustalić)" className="text-gray-500 font-bold leading-none text-[11px]">
                              ?MPZP{p.mpzp_unknown_length_m != null ? ` ${fmtMeters(p.mpzp_unknown_length_m)}` : ""}
                            </span>
                          )}
                          {p.protected_area === true && (
                            <span
                              title={`Obszary chronione w tej działce: ${(p.protected_area_types ?? []).map(protectedAreaLabel).join(", ") || "—"}`}
                              className="text-amber-600 font-bold leading-none text-[11px]"
                            >
                              ⛔OCHR{p.protected_area_length_m != null ? ` ${fmtMeters(p.protected_area_length_m)}` : ""}
                            </span>
                          )}
                        </div>
                        {p.obreb && (
                          <div className="text-[10px] text-[var(--color-text-muted)] mt-0.5 truncate max-w-[140px]">
                            {p.obreb}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => onFitToParcel(p.teryt)}
                          className="mt-1 inline-flex items-center gap-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
                          title="Przybliż mapę do tej działki"
                        >
                          <svg
                            width="10"
                            height="10"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            aria-hidden
                          >
                            <circle cx="6.5" cy="6.5" r="4.5" />
                            <line x1="10" y1="10" x2="14" y2="14" />
                            <line x1="6.5" y1="4" x2="6.5" y2="9" />
                            <line x1="4" y1="6.5" x2="9" y2="6.5" />
                          </svg>
                          Przybliż
                        </button>
                      </td>

                      {/* Własność: ULDK + override select + WPW input */}
                      <td className="px-2 py-1.5 align-top min-w-[220px] w-full">
                        {/* ULDK source (grey) */}
                        <div className="flex items-center gap-1 mb-1">
                          <span
                            className="inline-block w-2 h-2 shrink-0 ring-1 ring-[var(--color-border)]"
                            style={{
                              background: ownershipCodeColor(p.ownership_code),
                            }}
                          />
                          <span className="text-[var(--color-text-muted)] text-[10px]">
                            {p.ownership_code || "—"}
                          </span>
                        </div>

                        {/* Override select */}
                        <select
                          value={overrideValue}
                          onChange={(e) => {
                            const val = e.target.value;
                            const opt = findOwnershipOption(val);
                            onSaveDraft(p.teryt, {
                              ownership_override: val,
                              // Always apply the new option's wpwDefault when switching types.
                              wpw_count: val ? (opt?.wpwDefault ?? null) : null,
                            });
                          }}
                          className="w-full text-[10px] font-mono border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] px-1.5 py-1 focus:outline-none focus:border-[var(--color-primary)]"
                        >
                          <option value="">— brak nadpisania —</option>
                          <optgroup label="Kody ULDK">
                            {OWNERSHIP_OPTIONS.filter(
                              (o) => o.group === "uldk",
                            ).map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </optgroup>
                          <optgroup label="Inne">
                            {OWNERSHIP_OPTIONS.filter(
                              (o) => o.group === "manual",
                            ).map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </optgroup>
                        </select>

                        {/* Override label chip */}
                        {overrideOpt && (
                          <div className="flex items-center gap-1 mt-1">
                            <span
                              className="inline-block w-2 h-2 shrink-0 ring-1 ring-[var(--color-border)]"
                              style={{ background: overrideOpt.color }}
                            />
                            <span
                              className="text-[10px] font-semibold"
                              style={{ color: overrideOpt.color }}
                            >
                              {overrideOpt.label}
                            </span>
                          </div>
                        )}

                        {/* WPW count — only for WPW types */}
                        {isWpw && (
                          <div className="flex items-center gap-1.5 mt-1.5">
                            <span className="text-[10px] text-[var(--color-text-muted)] whitespace-nowrap">
                              Właściciele WPW:
                            </span>
                            <input
                              type="number"
                              min="0"
                              step="0.5"
                              value={draft?.wpw_count ?? wpwDefault}
                              onChange={(e) => {
                                const v = parseFloat(e.target.value);
                                if (!isNaN(v))
                                  onSaveDraft(p.teryt, { wpw_count: v });
                              }}
                              className="w-16 text-[10px] font-mono font-bold border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[var(--color-primary)] px-1.5 py-0.5 focus:outline-none focus:border-[var(--color-primary)]"
                            />
                          </div>
                        )}

                        {/* Element breakdown chips */}
                        {p.element_breakdown.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {p.element_breakdown.map((b, i) => (
                              <span
                                key={i}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] bg-[var(--color-bg-elevated)] border border-[var(--color-border-muted)]"
                              >
                                <span
                                  className="w-1 h-1 shrink-0"
                                  style={{ background: breakdownDot(b) }}
                                />
                                <span className="text-[var(--color-text-muted)]">
                                  {b.label}
                                </span>
                                <span className="text-[var(--color-text-primary)] font-bold">
                                  {breakdownValue(b)}
                                </span>
                              </span>
                            ))}
                          </div>
                        )}
                      </td>

                      {/* Trasa */}
                      <td className="px-2 py-1.5 align-top text-right whitespace-nowrap">
                        <div className="font-bold text-[var(--color-text-primary)]">
                          {fmtMeters(p.intersection_length_m)}
                        </div>
                        {p.intersection_length_m != null && (
                          <div className="relative h-[3px] w-full bg-[var(--color-border-muted)] mt-1">
                            <div
                              className="absolute right-0 top-0 h-full bg-[var(--color-primary)]"
                              style={{
                                width: `${(p.intersection_length_m / maxLen) * 100}%`,
                              }}
                            />
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── offices summary ── */}
          {officesSummary.length > 0 && (
            <div className="mt-4">
              <p className="section-label mb-1.5">Podsumowanie własności <span className="normal-case font-normal text-[var(--color-text-muted)]">(poza likwidacją)</span></p>
              <div className="border border-[var(--color-border)] overflow-hidden">
                <table className="w-full text-[11px] font-mono border-collapse">
                  <thead>
                    <tr className="bg-[var(--color-bg-elevated)]">
                      <th className="px-2.5 py-1.5 text-left font-bold uppercase tracking-widest text-[0.6rem] text-[var(--color-text-muted)] shadow-[inset_0_-1px_0_var(--color-border)]">
                        Typ własności
                      </th>
                      <th className="px-2.5 py-1.5 text-right font-bold uppercase tracking-widest text-[0.6rem] text-[var(--color-text-muted)] shadow-[inset_0_-1px_0_var(--color-border)]">
                        Dz.
                      </th>
                      <th className="px-2.5 py-1.5 text-right font-bold uppercase tracking-widest text-[0.6rem] text-[var(--color-text-muted)] shadow-[inset_0_-1px_0_var(--color-border)]">
                        Urzędów
                      </th>
                      <th className="px-2.5 py-1.5 text-right font-bold uppercase tracking-widest text-[0.6rem] text-[var(--color-text-muted)] shadow-[inset_0_-1px_0_var(--color-border)]">
                        WPW (zgody)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {officesSummary.map((row, i) => (
                      <tr
                        key={i}
                        className="border-t border-[var(--color-border-muted)] even:bg-[var(--color-bg-base)]"
                      >
                        <td className="px-2.5 py-1.5 text-[var(--color-text-primary)]">
                          {row.label}
                        </td>
                        <td className="px-2.5 py-1.5 text-right font-bold text-[var(--color-text-primary)]">
                          {row.parcels}
                        </td>
                        <td className="px-2.5 py-1.5 text-right text-[var(--color-text-secondary)]">
                          {row.offices !== null ? (
                            <span className="font-bold text-[var(--color-text-primary)]">
                              {row.offices}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-2.5 py-1.5 text-right text-[var(--color-text-secondary)]">
                          {row.wpwCount !== null ? (
                            <span className="font-bold text-emerald-600">
                              ~{row.wpwCount}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── internal-road neighbours (działki ościenne dróg wewnętrznych) ── */}
          {markedRoadTeryts.length > 0 && (
            <div className="mt-4">
              <p className="section-label mb-1.5">
                Sąsiedzi dróg wewnętrznych{" "}
                <span className="normal-case font-normal text-[var(--color-text-muted)]">
                  (działki ościenne, styk ≥ 0,5 m)
                </span>
              </p>
              <div className="border border-[var(--color-border)] p-2.5 text-[11px] space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[var(--color-text-secondary)]">
                    Oznaczone drogi wewn.:{" "}
                    <span className="font-bold text-[var(--color-text-primary)]">
                      {markedRoadTeryts.length}
                    </span>
                    {roadNeighborsInfo && (
                      <>
                        {" · "}ościenne prywatne w rysunku:{" "}
                        <span className="font-bold text-[var(--color-text-primary)]">
                          {roadNeighborsInfo.w_rysunku}
                        </span>
                        {" · "}poza rysunkiem:{" "}
                        <span className="font-bold text-red-600">
                          {roadNeighborsInfo.poza_rysunkiem}
                        </span>
                      </>
                    )}
                  </span>
                  {onRecomputeRoadNeighbors && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={onRecomputeRoadNeighbors}
                      disabled={!!roadNeighborsRunning}
                    >
                      {roadNeighborsRunning ? (
                        <>
                          <Spinner size="sm" /> Obliczanie…
                        </>
                      ) : (
                        "Przelicz sąsiadów dróg"
                      )}
                    </Button>
                  )}
                </div>
                {roadsMissingNeighbors.length > 0 && !roadNeighborsRunning && (
                  <p className="text-amber-600 dark:text-amber-500">
                    Brak policzonych sąsiadów dla:{" "}
                    {roadsMissingNeighbors.map((t) => shortTeryt(t)).join(", ")} — kliknij
                    „Przelicz sąsiadów dróg", aby klucze osc_dr_wewn były kompletne.
                  </p>
                )}
                {roadNeighborsTask?.status === "failure" && (
                  <p className="text-red-600">
                    Przeliczanie sąsiadów nie powiodło się: {roadNeighborsTask.error}
                  </p>
                )}
                {roadNeighbors.length > 0 && (
                  <div className="text-[var(--color-text-muted)] space-y-0.5">
                    {markedRoadTeryts
                      .filter((t) => roadsWithRows.has(t))
                      .map((t) => {
                        const rows = roadNeighbors.filter((r) => r.road_teryt === t);
                        const priv = rows.filter((r) =>
                          isCountedNeighbor(r.teryt, r.ownership_code),
                        );
                        const privInDrawing = priv.filter((r) =>
                          parcelTeryts.has(r.teryt),
                        ).length;
                        const pub = rows.length - priv.length;
                        return (
                          <p key={t}>
                            {shortTeryt(t)}: {rows.length} {pluralDzialka(rows.length)}{" "}
                            ościennych —{" "}
                            <span className="font-bold text-[var(--color-text-primary)]">
                              prywatne {priv.length}
                            </span>{" "}
                            ({privInDrawing} w rysunku, {priv.length - privInDrawing} poza)
                            {" · "}publiczne {pub}
                          </p>
                        );
                      })}
                    <p className="text-[10px]">
                      Do kluczy liczą się prywatne (kod 7/15) oraz działki o nieznanym
                      kodzie; publiczne i inne drogi wewn. są pomijane.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </SectionCard>
  );
}
